import { TypeManagerDecorator } from "../../main/type.map.service";
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { DataIngestionService, IngestionSubmission } from "./data.ingestion.service";
import { IngestionFormComponent } from "./ingestion.form.component";
import { LoginStore } from "../../stores/login.store";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { FormPublicIdentityComponent, type FormPublicIdentityPayload } from "./form.public.identity.component";
import { S25Util } from "../../util/s25-util";

type NewLinkLandingPhase = "idle" | "loadingPoint" | "formUnavailable" | "identity";

@TypeManagerDecorator("s25-ng-form-submission")
@Component({
    selector: "s25-ng-form-submission",
    template: `
        <div class="c-padding--double">
            @if (!loginStore.init()) {
                <div class="loading-message">Loading...</div>
            } @else if (requiresLogin() && !loginStore.loggedIn()) {
                <div class="sign-in-message">
                    <h2>Sign In Required</h2>
                    <p>Please sign in to access this form.</p>
                    <s25-ng-button [type]="'primary'" [onClick]="loginStore.login">Sign In</s25-ng-button>
                </div>
            } @else if (newLinkLandingPhase() === "loadingPoint" || loading()) {
                <div class="loading-message">Loading form...</div>
            } @else if (newLinkLandingPhase() === "formUnavailable") {
                <div class="unavailable-message">
                    <h2>Form Unavailable</h2>
                    <p>
                        This form is not available without signing in. Please contact the organization that shared the
                        link.
                    </p>
                </div>
            } @else if (newLinkLandingPhase() === "identity") {
                <s25-ng-form-public-identity (identitySubmit)="onPublicIdentitySubmit($event)" />
            } @else if (error()) {
                <div class="error-message">
                    <h2>Form Not Found</h2>
                    <p>{{ error() }}</p>
                </div>
            } @else if (submission()) {
                <div class="form-submission-container">
                    <h1>Form Submission</h1>
                    <s25-ng-ingestion-form
                        [submissionIdUuid]="submission()!.submissionIdUuid!"
                        [ingestionPointId]="submission()!.ingestionPointId!"
                        [readOnly]="submission()!.submitted || false"
                        (onSubmit)="onFormSubmit()"
                    ></s25-ng-ingestion-form>
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .loading-message,
        .error-message,
        .sign-in-message,
        .unavailable-message {
            text-align: center;
            padding: 3rem;
        }

        .error-message h2,
        .unavailable-message h2 {
            color: var(--color--text-danger);
            margin-bottom: 1rem;
        }

        .sign-in-message h2 {
            margin-bottom: 1rem;
            color: var(--color--link-text);
        }

        .sign-in-message p,
        .unavailable-message p {
            margin-bottom: 1.5rem;
        }

        .form-submission-container {
            max-width: 800px;
            margin: 0 auto;
        }

        .form-submission-container h1 {
            margin-bottom: 1rem;
            color: var(--color--link-text);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [IngestionFormComponent, S25ButtonComponent, FormPublicIdentityComponent],
})
export class FormSubmissionComponent implements OnInit {
    readonly loginStore = inject(LoginStore);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);
    readonly submission = signal<IngestionSubmission | null>(null);
    readonly requiresLogin = signal(false);
    readonly newLinkLandingPhase = signal<NewLinkLandingPhase>("idle");

    private loginHandled = false;
    private pendingNewLinkPointId: number | null = null;
    private newLinkLandingStarted = false;

    ngOnInit() {
        const hash = window.location.hash;
        const newMatch = hash.match(/(?:connect25\/)?form-submission\/new[?&]pointId=(\d+)/);
        const uuidMatch = hash.match(/(?:connect25\/)?form-submission\/([^/?]+)/);

        if (newMatch) {
            const pointId = Number(newMatch[1]);
            this.pendingNewLinkPointId = pointId;
            if (this.loginStore.init() && this.loginStore.loggedIn()) {
                this.loginHandled = true;
                void this.createAndLoadSubmission(pointId);
            }
        } else if (uuidMatch && uuidMatch[1] !== "new") {
            void this.loadSubmissionAndCheckAuth(uuidMatch[1]);
        } else {
            this.error.set("Invalid form link.");
        }
    }

    constructor() {
        effect(() => {
            if (!this.requiresLogin()) return;
            if (!this.loginStore.init()) return;
            if (this.loginHandled) return;
            if (this.pendingNewLinkPointId != null) return;

            if (!this.loginStore.loggedIn()) {
                this.loginHandled = true;
                void this.loginStore.login();
            }
        });

        effect(() => {
            const pointId = this.pendingNewLinkPointId;
            if (pointId == null) return;
            if (!this.loginStore.init()) return;
            if (this.loginStore.loggedIn()) return;
            if (this.newLinkLandingStarted) return;

            this.newLinkLandingStarted = true;
            void this.beginLoggedOutNewLinkLanding(pointId);
        });
    }

    private async beginLoggedOutNewLinkLanding(ingestionPointId: number) {
        try {
            this.newLinkLandingPhase.set("loadingPoint");
            this.error.set(null);
            const ingestionPoint = await DataIngestionService.getIngestionPoint(ingestionPointId);
            if (!ingestionPoint) {
                this.error.set("Form not found. It may have been deleted or the link may be invalid.");
                this.newLinkLandingPhase.set("idle");
                return;
            }
            if (!ingestionPoint.authless) {
                this.newLinkLandingPhase.set("formUnavailable");
                return;
            }
            this.newLinkLandingPhase.set("identity");
        } catch (err: unknown) {
            console.error("Failed to load form for public link:", err);
            this.error.set(S25Util.errorText(err, "Failed to load form. Please try again."));
            this.newLinkLandingPhase.set("idle");
        }
    }

    async onPublicIdentitySubmit(identity: FormPublicIdentityPayload) {
        const pointId = this.pendingNewLinkPointId;
        if (!pointId) return;

        try {
            this.loading.set(true);
            this.error.set(null);
            const created = await DataIngestionService.publicStartSubmission(pointId, identity);
            if (!created?.submissionIdUuid) {
                this.error.set("Could not start the form. Please try again.");
                this.newLinkLandingPhase.set("identity");
                return;
            }
            this.replaceHashWithSubmissionUuid(created.submissionIdUuid);
            this.newLinkLandingPhase.set("idle");
            this.pendingNewLinkPointId = null;
            this.submission.set(created);
        } catch (err: unknown) {
            console.error("Public form start failed:", err);
            this.error.set(
                S25Util.errorText(err, "Could not start the form. Please check your details and try again."),
            );
            this.newLinkLandingPhase.set("identity");
        } finally {
            this.loading.set(false);
        }
    }

    private replaceHashWithSubmissionUuid(submissionIdUuid: string) {
        const hash = `#!/form-submission/${submissionIdUuid}`;
        if (window.history.replaceState) {
            const base = `${window.location.pathname}${window.location.search}${hash}`;
            window.history.replaceState(null, "", base);
        } else {
            window.location.hash = hash;
        }
    }

    private async loadSubmissionAndCheckAuth(submissionIdUuid: string) {
        try {
            this.loading.set(true);
            this.error.set(null);
            const submission = await DataIngestionService.getSubmission(submissionIdUuid);
            if (!submission?.ingestionPointId) {
                this.error.set("Form not found. It may have been deleted or the link may be invalid.");
                return;
            }

            const ingestionPoint = await DataIngestionService.getIngestionPoint(submission.ingestionPointId);
            if (!ingestionPoint) {
                this.error.set("Form not found. It may have been deleted or the link may be invalid.");
                return;
            }

            if (ingestionPoint.authless) {
                this.submission.set(submission);
            } else {
                this.requiresLogin.set(true);
                this.submission.set(submission);
            }
        } catch (err: unknown) {
            console.error("Failed to load form submission:", err);
            this.error.set(S25Util.errorText(err, "Failed to load form. Please check your link and try again."));
        } finally {
            this.loading.set(false);
        }
    }

    async createAndLoadSubmission(ingestionPointId: number) {
        try {
            this.loading.set(true);
            this.error.set(null);
            this.newLinkLandingPhase.set("idle");
            this.pendingNewLinkPointId = null;
            const submission = await DataIngestionService.createSubmission(ingestionPointId);
            if (!submission?.submissionIdUuid) {
                this.error.set("Could not create a form session. Please try the link again.");
                return;
            }
            this.replaceHashWithSubmissionUuid(submission.submissionIdUuid);
            this.submission.set(submission);
        } catch (err: unknown) {
            console.error("Failed to create form submission:", err);
            this.error.set(S25Util.errorText(err, "Failed to load form. Please try again."));
        } finally {
            this.loading.set(false);
        }
    }

    onFormSubmit() {
        // Success message is rendered inline by s25-ng-ingestion-form.
    }
}
